import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI, logsAPI } from '../services/api';

const ACTION_BADGES = {
    create: { bg: '#d1fae5', color: '#065f46', label: 'Criado' },
    update: { bg: '#dbeafe', color: '#1e40af', label: 'Atualizado' },
    delete: { bg: '#fee2e2', color: '#991b1b', label: 'Deletado' },
};

const SOURCE_BADGES = {
    frontend: { bg: '#dbeafe', color: '#1e40af', label: 'Frontend' },
    'api-key': { bg: '#fef3c7', color: '#92400e', label: 'API Key' },
    auth: { bg: '#ede9fe', color: '#5b21b6', label: 'Auth' },
    public: { bg: '#f3f4f6', color: '#374151', label: 'Public' },
    unknown: { bg: '#f3f4f6', color: '#6b7280', label: '?' },
};

const STATUS_COLOR = (code) => {
    if (!code) return '#6b7280';
    if (code < 300) return '#059669';
    if (code < 400) return '#d97706';
    if (code < 500) return '#dc2626';
    return '#7c3aed';
};

function AuditTab() {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [actionFilter, setActionFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const limit = 30;

    const loadLogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = { limit, offset };
            if (actionFilter) params.action = actionFilter;
            const { data } = await auditAPI.getLogs(params);
            if (data.success) { setLogs(data.logs); setTotal(data.total); }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, [actionFilter, offset]);

    useEffect(() => { loadLogs(); }, [loadLogs]);

    return (
        <div>
            <div className="log-filters">
                <select className="ms-select" value={actionFilter} onChange={e => { setActionFilter(e.target.value); setOffset(0); }}>
                    <option value="">Todas Acoes</option>
                    <option value="create">Criacao</option>
                    <option value="update">Atualizacao</option>
                    <option value="delete">Exclusao</option>
                </select>
                <button className="btn btn-secondary btn-sm" onClick={loadLogs}>Atualizar</button>
            </div>

            {loading ? (
                <div className="loading"><div className="spinner"></div><span>Carregando...</span></div>
            ) : logs.length === 0 ? (
                <div className="audit-empty">Nenhum registro de auditoria encontrado.</div>
            ) : (
                <>
                    <table className="audit-table">
                        <thead>
                            <tr>
                                <th>Data/Hora</th>
                                <th>Usuario</th>
                                <th>Acao</th>
                                <th>Tipo</th>
                                <th>Zona</th>
                                <th>Detalhes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(log => {
                                const badge = ACTION_BADGES[log.action] || { bg: '#f3f4f6', color: '#374151', label: log.action };
                                let details = log.details;
                                try { details = JSON.parse(details); } catch {}
                                return (
                                    <tr key={log.id}>
                                        <td className="audit-date">{new Date(log.created_at + 'Z').toLocaleString('pt-BR')}</td>
                                        <td>{log.user_email || '-'}</td>
                                        <td><span className="badge" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span></td>
                                        <td>{log.entity_type}</td>
                                        <td>{log.zone_name || (log.zone_id ? log.zone_id.substring(0, 8) + '...' : '-')}</td>
                                        <td className="audit-details">
                                            {details && typeof details === 'object'
                                                ? <code>{JSON.stringify(details).substring(0, 120)}{JSON.stringify(details).length > 120 ? '...' : ''}</code>
                                                : <span>{details || '-'}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="audit-pagination">
                        <button className="btn btn-secondary btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Anterior</button>
                        <span>{offset + 1}-{Math.min(offset + limit, total)} de {total}</span>
                        <button className="btn btn-secondary btn-sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Proximo</button>
                    </div>
                </>
            )}
        </div>
    );
}

function RequestLogsTab() {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sourceFilter, setSourceFilter] = useState('');
    const [methodFilter, setMethodFilter] = useState('');
    const [pathFilter, setPathFilter] = useState('');
    const [offset, setOffset] = useState(0);
    const [expandedId, setExpandedId] = useState(null);
    const limit = 40;

    const loadLogs = useCallback(async () => {
        try {
            setLoading(true);
            const params = { limit, offset };
            if (sourceFilter) params.source = sourceFilter;
            if (methodFilter) params.method = methodFilter;
            if (pathFilter) params.path = pathFilter;
            const { data } = await logsAPI.getAll(params);
            if (data.success) { setLogs(data.logs); setTotal(data.total); }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, [sourceFilter, methodFilter, pathFilter, offset]);

    const loadStats = useCallback(async () => {
        try {
            const { data } = await logsAPI.getStats();
            if (data.success) setStats(data.stats);
        } catch {}
    }, []);

    useEffect(() => { loadLogs(); }, [loadLogs]);
    useEffect(() => { loadStats(); }, [loadStats]);

    return (
        <div>
            {/* Stats cards */}
            {stats && (
                <div className="log-stats-row">
                    <div className="log-stat-card">
                        <h4>Por Origem (24h)</h4>
                        <div className="log-stat-items">
                            {stats.bySource.map(s => {
                                const b = SOURCE_BADGES[s.source] || SOURCE_BADGES.unknown;
                                return (
                                    <div key={s.source} className="log-stat-item">
                                        <span className="badge" style={{ background: b.bg, color: b.color }}>{b.label}</span>
                                        <span className="log-stat-num">{s.total}</span>
                                        <span className="log-stat-avg">{Math.round(s.avg_duration)}ms</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="log-stat-card">
                        <h4>Por Status (24h)</h4>
                        <div className="log-stat-items">
                            {stats.byStatus.map(s => (
                                <div key={s.status_code} className="log-stat-item">
                                    <span style={{ color: STATUS_COLOR(s.status_code), fontWeight: 600, fontFamily: 'monospace' }}>{s.status_code}</span>
                                    <span className="log-stat-num">{s.total}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="log-stat-card">
                        <h4>Top Endpoints (24h)</h4>
                        <div className="log-stat-items log-stat-endpoints">
                            {stats.byPath.slice(0, 8).map((s, i) => (
                                <div key={i} className="log-stat-item">
                                    <span className="log-ep-method">{s.method}</span>
                                    <span className="log-ep-path">{s.path}</span>
                                    <span className="log-stat-num">{s.total}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="log-filters">
                <select className="ms-select" value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setOffset(0); }}>
                    <option value="">Todas Origens</option>
                    <option value="frontend">Frontend</option>
                    <option value="api-key">API Key</option>
                    <option value="auth">Auth</option>
                    <option value="public">Public</option>
                </select>
                <select className="ms-select" value={methodFilter} onChange={e => { setMethodFilter(e.target.value); setOffset(0); }}>
                    <option value="">Todos Metodos</option>
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                    <option value="DELETE">DELETE</option>
                </select>
                <input
                    type="text"
                    className="ms-search"
                    placeholder="Filtrar por path..."
                    value={pathFilter}
                    onChange={e => { setPathFilter(e.target.value); setOffset(0); }}
                    style={{ maxWidth: 200 }}
                />
                <button className="btn btn-secondary btn-sm" onClick={() => { loadLogs(); loadStats(); }}>Atualizar</button>
            </div>

            {/* Table */}
            {loading ? (
                <div className="loading"><div className="spinner"></div><span>Carregando...</span></div>
            ) : logs.length === 0 ? (
                <div className="audit-empty">Nenhum log encontrado.</div>
            ) : (
                <>
                    <table className="audit-table log-table">
                        <thead>
                            <tr>
                                <th>Data/Hora</th>
                                <th>Metodo</th>
                                <th>Path</th>
                                <th>Status</th>
                                <th>Duracao</th>
                                <th>Origem</th>
                                <th>Usuario/Key</th>
                                <th>IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(log => {
                                const srcBadge = SOURCE_BADGES[log.source] || SOURCE_BADGES.unknown;
                                return (
                                    <React.Fragment key={log.id}>
                                        <tr
                                            className={`log-row ${log.status_code >= 400 ? 'log-row-error' : ''}`}
                                            onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td className="audit-date">{new Date(log.created_at + 'Z').toLocaleString('pt-BR')}</td>
                                            <td><span className="log-method" data-method={log.method}>{log.method}</span></td>
                                            <td className="log-path">{log.path}</td>
                                            <td><span style={{ color: STATUS_COLOR(log.status_code), fontWeight: 600, fontFamily: 'monospace' }}>{log.status_code}</span></td>
                                            <td className="log-duration">{log.duration_ms != null ? `${log.duration_ms}ms` : '-'}</td>
                                            <td><span className="badge" style={{ background: srcBadge.bg, color: srcBadge.color, fontSize: '0.65rem' }}>{srcBadge.label}</span></td>
                                            <td className="log-user">{log.user_email || log.api_key_name || '-'}</td>
                                            <td className="log-ip">{log.ip || '-'}</td>
                                        </tr>
                                        {expandedId === log.id && (
                                            <tr className="log-expanded">
                                                <td colSpan="8">
                                                    <div className="log-detail-grid">
                                                        {log.user_agent && (
                                                            <div className="log-detail-item">
                                                                <strong>User-Agent:</strong>
                                                                <code>{log.user_agent}</code>
                                                            </div>
                                                        )}
                                                        {log.request_body && (
                                                            <div className="log-detail-item">
                                                                <strong>Request Body:</strong>
                                                                <pre>{log.request_body}</pre>
                                                            </div>
                                                        )}
                                                        {log.response_summary && (
                                                            <div className="log-detail-item">
                                                                <strong>Response:</strong>
                                                                <code>{log.response_summary}</code>
                                                            </div>
                                                        )}
                                                        {log.error && (
                                                            <div className="log-detail-item log-detail-error">
                                                                <strong>Erro:</strong>
                                                                <code>{log.error}</code>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="audit-pagination">
                        <button className="btn btn-secondary btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>Anterior</button>
                        <span>{offset + 1}-{Math.min(offset + limit, total)} de {total}</span>
                        <button className="btn btn-secondary btn-sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Proximo</button>
                    </div>
                </>
            )}
        </div>
    );
}

function AuditLog() {
    const [tab, setTab] = useState('requests');

    return (
        <div className="audit-log">
            <div className="audit-header">
                <div>
                    <h3>Logs do Sistema</h3>
                    <p>Monitoramento completo de todas as chamadas API e operacoes CRUD.</p>
                </div>
            </div>

            <div className="log-tabs">
                <button className={`log-tab ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>
                    Request Logs
                </button>
                <button className={`log-tab ${tab === 'audit' ? 'active' : ''}`} onClick={() => setTab('audit')}>
                    Audit Trail (CRUD)
                </button>
            </div>

            {tab === 'requests' ? <RequestLogsTab /> : <AuditTab />}
        </div>
    );
}

export default AuditLog;
